import { useEffect, useRef } from "react";
import { useAuth } from "./auth";
import { toast } from "sonner";
import i18n from "../i18n";
import api from "./api";
import { setAdminPendingCount, resetAdminPendingCount } from "./adminPendingStore";
import { setDealerQuickCounts } from "./dealerQuickCountsStore";
import { setSavedSearchUnread, resetSavedSearchUnread } from "./savedSearchUnreadStore";

/**
 * Opens a per-user WebSocket channel and surfaces server push events.
 * - In-app: shows a sonner toast for every saved_search_match event.
 * - System: fires a native `Notification` when permission was granted and
 *   the tab is currently hidden (so we don't double-notify the user).
 *
 * Auto-reconnects with exponential backoff (max 30 s). Strict-Mode safe:
 * each effect-instance owns its own socket closure so the cleanup never
 * closes a socket created by a later run.
 */
export default function useUserNotifications() {
    const { user } = useAuth();
    const reconnectAttemptsRef = useRef(0);

    useEffect(() => {
        if (!user || user === false || !user.id) {
            resetAdminPendingCount();
            resetSavedSearchUnread();
            return;
        }

        const isAdmin = user.role === "admin";
        const canHaveSavedSearches = !isAdmin; // b2c and b2b only

        // Initial fetch of pending-count for admins (independent of the WS).
        if (isAdmin) {
            api.get("/admin/dealers/pending-count")
                .then((r) => setAdminPendingCount(r.data?.count ?? 0))
                .catch(() => { /* silent — pill stays hidden */ });
        } else {
            resetAdminPendingCount();
        }
        if (canHaveSavedSearches) {
            api.get("/saved-searches/unread-count")
                .then((r) => setSavedSearchUnread(r.data?.count ?? 0))
                .catch(() => { /* silent */ });
        } else {
            resetSavedSearchUnread();
        }

        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const wsUrl = backendUrl.replace(/^http/, "ws") + `/api/ws/user/${user.id}`;

        let ws = null;
        let pingId = null;
        let reconnectTimer = null;
        let stopped = false;

        const handleMatch = (payload) => {
            const t = i18n.t.bind(i18n);
            const title = t("notify.saved_search_title", { name: payload.search_name });
            const body = t("notify.saved_search_body", { count: payload.count });

            toast.success(title, {
                description: body,
                action: {
                    label: t("notify.open_label"),
                    onClick: () => { window.location.href = "/dashboard?tab=saved-searches"; },
                },
            });

            if (
                typeof window !== "undefined" &&
                "Notification" in window &&
                Notification.permission === "granted" &&
                document.visibilityState === "hidden"
            ) {
                try {
                    const n = new Notification(title, {
                        body,
                        tag: `saved_search_${payload.search_id}`,
                        icon: "/favicon.ico",
                    });
                    n.onclick = () => {
                        window.focus();
                        window.location.href = "/dashboard?tab=saved-searches";
                        n.close();
                    };
                } catch (_) { /* user agent might block */ }
            }
        };

        // Web-Push for inbound chat messages — fires only when tab is hidden.
        const pushMessage = (payload) => {
            if (payload.from_user_id === user.id) return; // don't notify the sender
            if (typeof window === "undefined" || !("Notification" in window)) return;
            if (Notification.permission !== "granted") return;
            if (document.visibilityState !== "hidden") return;
            try {
                const title = `Neue Nachricht von ${payload.from_name || "TwoWheels"}`;
                const n = new Notification(title, {
                    body: (payload.content || "").slice(0, 140),
                    tag: `chat_${payload.thread_id}`,
                    icon: "/favicon.ico",
                });
                n.onclick = () => {
                    window.focus();
                    window.location.href = `/dashboard?tab=messages&thread=${payload.thread_id}`;
                    n.close();
                };
            } catch (_) { /* ignored */ }
        };

        // Web-Push for offer state changes
        const pushOffer = (payload) => {
            if (typeof window === "undefined" || !("Notification" in window)) return;
            if (Notification.permission !== "granted") return;
            if (document.visibilityState !== "hidden") return;
            const titleByType = {
                offer_created: "Neues Preisangebot",
                offer_countered: "Gegenangebot eingegangen",
                offer_accepted: "Angebot angenommen",
                offer_rejected: "Angebot abgelehnt",
                offer_auto_rejected: "Angebot automatisch abgelehnt",
                offer_withdrawn: "Angebot zurückgezogen",
            };
            const title = titleByType[payload.type];
            if (!title) return;
            try {
                const amount = payload.final_amount || payload.counter_amount || payload.amount;
                const body = amount ? `Betrag: ${Number(amount).toLocaleString("de-DE")} €` : "Details im Dashboard ansehen";
                const n = new Notification(title, {
                    body,
                    tag: `offer_${payload.offer_id}`,
                    icon: "/favicon.ico",
                });
                n.onclick = () => {
                    window.focus();
                    window.location.href = `/dashboard?tab=offers`;
                    n.close();
                };
            } catch (_) { /* ignored */ }
        };

        const connect = () => {
            if (stopped) return;
            try {
                ws = new WebSocket(wsUrl);
            } catch (_) {
                return;
            }

            ws.onopen = () => {
                reconnectAttemptsRef.current = 0;
                pingId = setInterval(() => {
                    if (ws && ws.readyState === WebSocket.OPEN) {
                        try { ws.send("ping"); } catch (_) { /* ignore */ }
                    }
                }, 30000);
            };

            ws.onmessage = (ev) => {
                let payload;
                try { payload = JSON.parse(ev.data); } catch (_) { return; }
                if (!payload || !payload.type) return;
                if (payload.type === "saved_search_match") {
                    if (typeof payload.unread_total === "number") {
                        setSavedSearchUnread(payload.unread_total);
                    }
                    // Only show toast/notification for actual match events,
                    // not synthetic "cleared" broadcasts (search_id === null).
                    if (payload.search_id) handleMatch(payload);
                }
                if (payload.type === "verification_count_changed" && isAdmin) {
                    setAdminPendingCount(payload.pending_count);
                }
                // Jun-2026 (User-Wunsch) — Live-Badge-Update der Händler-Schnellzugriff-Kacheln.
                if (payload.type === "dealer_quick_counts_changed") {
                    setDealerQuickCounts({
                        workshop_open: payload.workshop_open,
                        inventory_uncounted: payload.inventory_uncounted,
                    });
                }
                if (payload.type === "chat_message") {
                    pushMessage(payload);
                }
                if (payload.type && payload.type.startsWith("offer_")) {
                    pushOffer(payload);
                }
                // Feb-2026 (User-Wunsch 24 May Iter 18): Echtzeit-Toast +
                // System-Notification beim allerersten QR-Scan eines Inserats.
                if (payload.type === "qr_first_scan") {
                    const num = payload.internal_number || (payload.listing_id || "").slice(0, 8);
                    const titleStr = "🎯 Dein Foto wurde gerade gescannt!";
                    const bodyStr = `Erster QR-Scan auf Inserat ${num} — jemand hat den QR-Code außerhalb der Plattform entdeckt.`;
                    toast.success(titleStr, {
                        description: bodyStr,
                        duration: 8000,
                        action: {
                            label: "Inserat öffnen",
                            onClick: () => { window.location.href = `/listing/${payload.listing_id}`; },
                        },
                    });
                    if (
                        typeof window !== "undefined" &&
                        "Notification" in window &&
                        Notification.permission === "granted" &&
                        document.visibilityState === "hidden"
                    ) {
                        try {
                            const n = new Notification(titleStr, {
                                body: bodyStr,
                                tag: `qr_first_scan_${payload.listing_id}`,
                                icon: "/favicon.ico",
                            });
                            n.onclick = () => {
                                window.focus();
                                window.location.href = `/listing/${payload.listing_id}`;
                                n.close();
                            };
                        } catch (_) { /* ignored */ }
                    }
                }
            };

            ws.onclose = () => {
                if (pingId) { clearInterval(pingId); pingId = null; }
                if (stopped) return;
                const delay = Math.min(30000, 1000 * 2 ** reconnectAttemptsRef.current);
                reconnectAttemptsRef.current += 1;
                reconnectTimer = setTimeout(connect, delay);
            };

            ws.onerror = () => {
                try { ws.close(); } catch (_) { /* ignore */ }
            };
        };

        connect();

        return () => {
            stopped = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (pingId) clearInterval(pingId);
            if (ws) {
                // Clear handlers first so onclose doesn't schedule a reconnect.
                ws.onclose = null;
                ws.onmessage = null;
                ws.onerror = null;
                // If the socket is still CONNECTING, the browser logs a noisy
                // "closed before connect" warning when we call close(). Defer
                // the close until after onopen fires so the handshake can finish
                // cleanly. Real teardown (page navigate) is still immediate.
                if (ws.readyState === WebSocket.CONNECTING) {
                    ws.onopen = () => {
                        try { ws.close(1000, "component-unmount-deferred"); } catch (_) { /* ignore */ }
                    };
                } else {
                    ws.onopen = null;
                    try { ws.close(1000, "component-unmount"); } catch (_) { /* ignore */ }
                }
            }
        };
    }, [user]);
}

/**
 * Trigger the browser permission prompt. Returns a promise that resolves to
 * the new permission state ("granted" | "denied" | "default" | "unsupported").
 */
export async function requestNotificationPermission() {
    if (typeof window === "undefined" || !("Notification" in window)) {
        return "unsupported";
    }
    if (Notification.permission === "granted" || Notification.permission === "denied") {
        return Notification.permission;
    }
    try {
        return await Notification.requestPermission();
    } catch (_) {
        return Notification.permission;
    }
}
